import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Icon } from '@/lib/icons';
import {
  AuthHeading,
  AuthNotice,
  AuthPasswordToggle,
  AuthShell,
  AuthSpinner,
  AUTH_FIELD_CLASS,
  AUTH_PRIMARY_BUTTON_CLASS,
  AUTH_SECONDARY_BUTTON_CLASS,
} from '@/features/auth-shell';
import { PasswordRecoveryCopy, PasswordRecoveryStep } from './password-recovery.types';
import { usePasswordRecovery } from './use-password-recovery';

type PasswordRecoveryViewProps = {
  t: PasswordRecoveryCopy;
  recovery: ReturnType<typeof usePasswordRecovery>;
};

const ICON_CLASS = 'pointer-events-none absolute left-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground';

export function PasswordRecoveryView({ t, recovery }: PasswordRecoveryViewProps) {
  return (
    <AuthShell documentTitle={t.forgotPasswordTitle.replace(/\s*\?$/, '')}>
      <RecoveryPanel t={t} recovery={recovery} />
      <p className="text-center text-xs leading-relaxed text-faint-foreground">{t.securityNote}</p>
    </AuthShell>
  );
}

function RecoveryPanel({ t, recovery }: PasswordRecoveryViewProps) {
  const panels: Record<PasswordRecoveryStep, ReactNode> = {
    request: <RequestPanel t={t} recovery={recovery} />,
    'email-sent': <DeliveryPanel t={t} recovery={recovery} />,
    otp: <OtpPanel t={t} recovery={recovery} />,
    'new-password': <NewPasswordPanel t={t} recovery={recovery} />,
    success: <SuccessPanel t={t} recovery={recovery} />,
  };

  return <div className="space-y-7" data-testid={`recovery-step-${recovery.step}`}>{panels[recovery.step] ?? panels.request}</div>;
}

function RequestPanel({ t, recovery }: PasswordRecoveryViewProps) {
  return (
    <>
      <AuthHeading title={t.forgotPasswordTitle} description={t.forgotPasswordSubtitle} />
      <form onSubmit={recovery.requestReset} className="space-y-5" data-testid="forgot-password-form">
        <div className="space-y-2">
          <Label htmlFor="identifier" className="text-sm font-medium">{t.passwordRecoveryIdentifierLabel}</Label>
          <div className="relative">
            <Icon name="userCheck" className={ICON_CLASS} />
            <Input
              id="identifier"
              value={recovery.identifier}
              onChange={(event) => recovery.setIdentifier(event.target.value)}
              placeholder={t.passwordRecoveryIdentifierPlaceholder}
              autoComplete="username"
              aria-describedby="identifier-help"
              className={`pl-10 ${AUTH_FIELD_CLASS}`}
              required
            />
          </div>
          <p id="identifier-help" className="text-xs leading-relaxed text-muted-foreground">{t.passwordRecoveryIdentifierHelp}</p>
        </div>

        <Button type="submit" disabled={recovery.loading} className={AUTH_PRIMARY_BUTTON_CLASS}>
          {recovery.loading ? <LoadingLabel label={t.sending} /> : <ButtonLabel label={t.sendLink} icon="arrowRight" />}
        </Button>
      </form>
      <FooterLinks t={t} currentLang={recovery.currentLang} />
    </>
  );
}

function DeliveryPanel({ t, recovery }: PasswordRecoveryViewProps) {
  const isUnknown = recovery.method === 'unknown';
  return (
    <>
      <AuthHeading
        title={isUnknown ? t.passwordRecoveryUnknownTitle : t.passwordRecoveryEmailSentTitle}
        description={isUnknown ? t.passwordRecoveryUnknownDesc : t.passwordRecoveryEmailSentDesc}
      />
      <div className="space-y-4">
        {!isUnknown && <ChannelNotice icon="mail" target={recovery.maskedTarget} />}
        {recovery.formattedRemaining && <TimerLine t={t} value={recovery.formattedRemaining} />}
        {isUnknown && (
          <Button type="button" variant="outline" onClick={recovery.enterSmsCode} className={`${AUTH_SECONDARY_BUTTON_CLASS} w-full`}>
            <Icon name="phone" className="mr-2 h-4 w-4" />{t.passwordRecoveryEnterSmsCode}
          </Button>
        )}
        <Button type="button" onClick={recovery.goToLogin} className={AUTH_PRIMARY_BUTTON_CLASS}>
          <Icon name="arrowLeft" className="mr-2 h-4 w-4" />{t.backToLogin}
        </Button>
        <Button type="button" variant="outline" onClick={recovery.restart} className={`${AUTH_SECONDARY_BUTTON_CLASS} w-full`}>
          {t.passwordRecoveryResendLink}
        </Button>
      </div>
    </>
  );
}

function OtpPanel({ t, recovery }: PasswordRecoveryViewProps) {
  return (
    <>
      <AuthHeading title={t.passwordRecoverySmsSentTitle} description={t.passwordRecoverySmsSentDesc} />
      <ChannelNotice icon="phone" target={recovery.maskedTarget} />
      <form onSubmit={recovery.verifyOtp} className="space-y-5">
        <div className="space-y-2">
          <Label htmlFor="otp" className="text-sm font-medium">{t.passwordRecoveryOtpLabel}</Label>
          <Input
            id="otp"
            inputMode="numeric"
            pattern="[0-9]*"
            autoComplete="one-time-code"
            value={recovery.otp}
            onChange={(event) => recovery.setOtp(event.target.value)}
            placeholder={t.passwordRecoveryOtpPlaceholder}
            className={`${AUTH_FIELD_CLASS} h-14 text-center text-xl font-semibold tracking-[0.35em]`}
            maxLength={6}
          />
        </div>
        {recovery.formattedRemaining && <TimerLine t={t} value={recovery.formattedRemaining} />}
        <Button type="submit" disabled={!recovery.canVerifyOtp} className={AUTH_PRIMARY_BUTTON_CLASS}>
          {recovery.loading ? <LoadingLabel label={t.sending} /> : <ButtonLabel label={t.passwordRecoveryVerifyOtp} icon="check" />}
        </Button>
      </form>
      <button
        type="button"
        onClick={() => recovery.requestReset()}
        className="w-full rounded text-sm font-medium text-brand transition-colors hover:underline focus-ring"
      >
        {t.passwordRecoveryResendCode}
      </button>
    </>
  );
}

function NewPasswordPanel({ t, recovery }: PasswordRecoveryViewProps) {
  return (
    <>
      <AuthHeading
        eyebrow={t.passwordRecoveryOtpVerifiedTitle}
        title={t.passwordRecoveryNewPasswordTitle}
        description={t.passwordRecoveryNewPasswordDesc}
      />
      <form onSubmit={recovery.submitPassword} className="space-y-5">
        <PasswordField id="new-password" label={t.resetPasswordNewLabel} value={recovery.password} visible={recovery.showPassword} onChange={recovery.setPassword} onToggle={() => recovery.setShowPassword((value) => !value)} />
        <PasswordField id="confirm-password" label={t.resetPasswordConfirmLabel} value={recovery.confirmPassword} visible={recovery.showConfirmPassword} onChange={recovery.setConfirmPassword} onToggle={() => recovery.setShowConfirmPassword((value) => !value)} />
        <PasswordCriteria t={t} recovery={recovery} />
        <Button type="submit" disabled={!recovery.canSubmitPassword} className={AUTH_PRIMARY_BUTTON_CLASS}>
          {recovery.loading ? <LoadingLabel label={t.resetPasswordLoading} /> : <ButtonLabel label={t.resetPasswordSubmit} icon="check" />}
        </Button>
      </form>
    </>
  );
}

function SuccessPanel({ t, recovery }: PasswordRecoveryViewProps) {
  return (
    <>
      <AuthHeading title={t.resetPasswordSuccessTitle} />
      <AuthNotice tone="success" icon={<Icon name="check" className="h-5 w-5" />}>
        {t.resetPasswordSuccessDesc}
      </AuthNotice>
      <Button type="button" onClick={recovery.goToLogin} className={AUTH_PRIMARY_BUTTON_CLASS}>
        {t.backToLogin}
      </Button>
    </>
  );
}

function PasswordField({ id, label, value, visible, onChange, onToggle }: { id: string; label: string; value: string; visible: boolean; onChange: (value: string) => void; onToggle: () => void }) {
  return (
    <div className="space-y-2">
      <Label htmlFor={id} className="text-sm font-medium">{label}</Label>
      <div className="relative">
        <Icon name="lock" className={ICON_CLASS} />
        <Input id={id} type={visible ? 'text' : 'password'} value={value} onChange={(event) => onChange(event.target.value)} required minLength={8} placeholder="••••••••" className={`pl-10 pr-11 ${AUTH_FIELD_CLASS}`} autoComplete="new-password" />
        <AuthPasswordToggle visible={visible} onToggle={onToggle} />
      </div>
    </div>
  );
}

function PasswordCriteria({ t, recovery }: PasswordRecoveryViewProps) {
  return (
    <div className="space-y-2.5 rounded-2xl border border-border/70 bg-muted/30 p-4">
      <p className="text-xs font-semibold uppercase tracking-[0.14em] text-muted-foreground">{t.resetPasswordCriteria}</p>
      <ul className="grid gap-2 text-xs sm:grid-cols-2" aria-live="polite">
        <Criterion checked={recovery.passwordHasMinLength} label={t.resetPasswordAtLeast6} />
        <Criterion checked={recovery.passwordHasLetterCase} label={t.resetPasswordUpperLower} />
        <Criterion checked={recovery.passwordHasDigitAndSpecial} label={t.resetPasswordDigitSpecial} />
        <Criterion checked={recovery.passwordsMatch} label={t.resetPasswordMatch} />
      </ul>
    </div>
  );
}

function Criterion({ checked, label }: { checked: boolean; label: string }) {
  return <li className={`flex items-center gap-2 transition-colors ${checked ? 'text-success' : 'text-muted-foreground'}`}><Icon name={checked ? 'check' : 'x'} className="h-3.5 w-3.5 shrink-0" /><span>{label}</span></li>;
}

function ChannelNotice({ icon, target }: { icon: 'mail' | 'phone'; target: string | null }) {
  return (
    <AuthNotice tone="brand" icon={<Icon name={icon} className="h-5 w-5" />} title={<span className="break-all">{target || '••••••'}</span>} />
  );
}

function TimerLine({ t, value }: { t: PasswordRecoveryCopy; value: string }) {
  return <div className="flex items-center justify-between rounded-xl border border-border/70 px-4 py-2.5 text-sm text-muted-foreground"><span>{t.passwordRecoveryExpiresIn}</span><strong className="tabular-nums text-foreground">{value}</strong></div>;
}

function LoadingLabel({ label }: { label: string }) {
  return <span className="inline-flex items-center justify-center gap-2"><AuthSpinner />{label}</span>;
}

function ButtonLabel({ label, icon }: { label: string; icon: 'arrowRight' | 'check' }) {
  return <span className="inline-flex items-center justify-center gap-2"><span>{label}</span><Icon name={icon} className="h-4 w-4" /></span>;
}

function FooterLinks({ t, currentLang }: { t: PasswordRecoveryCopy; currentLang: string }) {
  return (
    <div className="border-t border-border/70 pt-6 text-center">
      <Link to={`/${currentLang}/login`} className="inline-flex items-center gap-1.5 rounded text-sm font-medium text-muted-foreground transition-colors hover:text-foreground focus-ring">
        <Icon name="arrowLeft" className="h-3.5 w-3.5" />{t.backToLogin}
      </Link>
    </div>
  );
}
